import { useEffect, useRef, type RefObject } from 'react';

/**
 * The collapse state of one titled nav section: the `defaultOpen` it mounts with, and the
 * persistence of whatever the user does to it afterwards.
 *
 * A section restores the viewer's own last state from `localStorage` and falls back to the
 * rail's `defaultOpen` when there is nothing stored (`design/40-shell-and-pages.md`,
 * "Sidebar"), which is why this is a hook
 * rather than a piece of component state: Primer's `NavList.Item` reads `defaultOpen` once
 * at mount and exposes no controlled `open` prop, so the stored value has to be in hand
 * before the first render and the new value has to be read back off the DOM. Primer flips
 * its own open state on click and updates the toggle button's `aria-expanded`; we read that
 * on the next tick and store it. Clicks on the section's rows bubble to the same handler,
 * but they do not change the section's expanded state, so persisting the unchanged value is
 * a no-op.
 *
 * **Each rail passes its own `storageKey`.** The DMA dashboard's rail and the unified
 * investor rail have sections of the same name in different products, so one shared key
 * would let a section a user collapsed in one rail open collapsed in the other.
 *
 * The wrapper is the caller's, deliberately: the two rails mount different section
 * wrappers, and this hook is only the storage half. Spread it onto whichever wrapper the
 * rail uses — `ref` so the handler can find the toggle button, `onSectionClick` on the
 * wrapper rather than the button because the button is Primer's and we never hold it.
 *
 * A stored `false` does not always win. Primer's own layout effect looks for an
 * `aria-current` row inside the section's `SubNav` and opens the section when it finds one,
 * after `defaultOpen` has been read — so the section holding the page the user is on is
 * always open, whatever they last did to it. That is deliberate on Primer's side and is the
 * behaviour both rails have shipped with; see `design/40-shell-and-pages.md`, "Sidebar".
 * A rail that passes `holdsCurrentPage` also stores that forced open as the section's state, so
 * the section stays open on the next page rather than closing again behind the user.
 *
 * **`defaultOpen` is the rail's call, not this hook's**, and the two rails answer it
 * differently: the DMA dashboard opens collapsed, so a freemium user meets a short rail
 * rather than a wall of rows (PLT-5229), while the unified investor rail opens expanded,
 * because its rows *are* what that shell offers and a reader who has to open three sections
 * to find them has been given a menu instead of a rail (PLT-6696). It only ever decides the
 * first visit: from the viewer's first toggle onwards the stored value answers instead.
 */
export function useNavSectionCollapse({
  storageKey,
  sectionKey,
  defaultOpen = false,
  holdsCurrentPage = false,
}: {
  storageKey: string;
  sectionKey: string;
  defaultOpen?: boolean;
  holdsCurrentPage?: boolean;
}): {
  ref: RefObject<HTMLDivElement>;
  onSectionClick: () => void;
  defaultOpen: boolean;
} {
  const ref = useRef<HTMLDivElement>(null);
  const initialOpenRef = useRef<boolean>(readNavOpenState(storageKey)[sectionKey] ?? defaultOpen);

  useEffect(() => {
    if (holdsCurrentPage) writeNavSectionOpen(storageKey, sectionKey, true);
  }, [holdsCurrentPage, storageKey, sectionKey]);

  const onSectionClick = () => {
    const el = ref.current;
    if (!el) return;
    window.setTimeout(() => {
      const button = el.querySelector('button[aria-expanded]');
      if (!button) return;
      writeNavSectionOpen(storageKey, sectionKey, button.getAttribute('aria-expanded') === 'true');
    }, 0);
  };

  return { ref, onSectionClick, defaultOpen: initialOpenRef.current };
}

type NavOpenState = Record<string, boolean>;

function readNavOpenState(storageKey: string): NavOpenState {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as NavOpenState) : {};
  } catch {
    return {};
  }
}

function writeNavSectionOpen(storageKey: string, sectionKey: string, isOpen: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify({ ...readNavOpenState(storageKey), [sectionKey]: isOpen }));
  } catch {
    // localStorage may be unavailable (private mode / quota) — fall back to a session-only
    // preference. Collapsing still works; we just don't persist it across reloads.
  }
}
